import { Borrower } from 'app/modules/borrower/models/borrower/borrower.model';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { WorkflowService } from 'app/modules/borrower/services/workflow.service';
import { first, finalize, filter, map, switchMap } from 'rxjs/operators';
import { BorrowerState } from 'app/store/borrower/borrower/borrower.state';
import { Select } from '@ngxs/store';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { WorkflowTasksState } from 'app/services/workflow-tasks/workflow-tasks.state';
import { notNull } from 'app/modules/shared/functions/filters';

export interface SentToFundingDeskModel {
    sent: boolean;
    modelErrorTitle?: string | null;
    modelErrorBody?: string | null;
}

@Component({
    selector: 'submit-app-button',
    templateUrl: 'submit-app.component.html',
    styleUrls: ['submit-app.component.scss'],
    standalone: false
})

export class SubmitAppComponent implements OnInit {
    @Select(BorrowerState.borrower) borrower$: Observable<Borrower>;
    @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
    @Select(WorkflowTasksState.incompleteKickbacks) incompleteKickbacks$: Observable<Task>;

    @Input() borrowerId: number;
    @Input() onAppReqPanel: boolean;
    @Input() disabled: boolean = false;
    @Output() submitted = new EventEmitter();

    errorModelTitle: string;
    errorModelBody: string;

    allComplete: boolean | null;
    sending = false;
    visible$: Observable<boolean>;

    constructor(private workflowService: WorkflowService) { }

    /**
     * Ng lifecycle
     * @return void
     */
    ngOnInit(): void {
        const inValidStatus$ = this.borrower$.pipe(
            filter(notNull),
            map((borrower) => borrower?.status ? ['inContact', 'dealInProgress'].includes(borrower.status) : false)
        );

        const assignedToBorrower$ = combineLatest([
            this.lenderUser$, this.borrower$
        ]).pipe(
            map(([
                currentUser,
                borrower
            ]) => currentUser?.id === borrower?.userId || currentUser.permissions.includes('overrideAppReqsForSend')));

        const hasPermission$ = this.lenderUser$.pipe(
            map((currentUser) => currentUser.permissions.includes('sendToFundingDesk'))
        );

        this.visible$ = combineLatest([
            inValidStatus$,
            assignedToBorrower$,
            hasPermission$,
            this.incompleteKickbacks$
        ]).pipe(
            map(([
                borrowerInValidStatus,
                currentUserIsAssignedToBorrower,
                hasPermission,
                incompleteKickbacks
            ]) => (
                borrowerInValidStatus
                && currentUserIsAssignedToBorrower
                && hasPermission
                && !incompleteKickbacks
            ))
        );
    }

    sendToFundingDesk() {
        const visible = this.visible$.subscribe((boolean) => boolean);
        if (visible && !this.disabled) {
            this.sending = true;
            this.borrower$.pipe(
                filter(notNull),
                first(),
                switchMap(borrower => this.workflowService.sendToFundingDesk(borrower)),
                finalize(() => this.sending = false)
            ).subscribe({
                next: () => {
                    const sentToFundingDeskModel: SentToFundingDeskModel = { sent: true };
                    this.submitted.emit(sentToFundingDeskModel);
                },
                error: (errors) => {
                    if (errors.length > 0) {
                        const error = errors[0].code;
                        if (error === 'ALREADY_SENT') {
                            this.errorModelTitle = 'Borrower already sent';
                            this.errorModelBody = `This borrower has already been sent
                                to the funding desk. Create a funding desk request instead.`;

                        } else if (error === 'REQUIREMENTS_NOT_MET') {
                            this.errorModelTitle = 'Application requirements not met';
                            this.errorModelBody = `The application requirements have not
                                yet been met for this borrower. Finish the
                                application and try again. You may need to refresh
                                the page to see the current requirements.`;

                        } else if (error === 'PPP_AUTOSTART_INVALID') {
                            this.errorModelTitle = 'PPP Deal requirements not met';
                            this.errorModelBody = 'PPP Deal requirements not met: ' + errors[0].message;
                        } else {
                            this.errorModelTitle = 'Failed to send to Funding Desk';
                            this.errorModelBody = 'Please try again in a few minutes.';
                        }
                    } else {
                        this.errorModelTitle = 'Failed to send to Funding Desk';
                        this.errorModelBody = 'Please try again in a few minutes.';
                    }
                    const sentToFundingDeskModel: SentToFundingDeskModel = {
                        sent: false,
                        modelErrorTitle: this.errorModelTitle,
                        modelErrorBody: this.errorModelBody
                    };
                    this.submitted.emit(sentToFundingDeskModel);
                }
            });
        }
    }
}
